import { useEffect, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import type { BriefingListItemDTO } from '@contracts/assistant-proactive';
import type { Id } from '@contracts/common';
import { formatDateTime } from '@/components/DateField';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAssistant } from '../AssistantProvider';
import { useBriefing, useBriefingList } from './api';
import { BriefingView } from './BriefingView';

/**
 * "Briefing trước" in the Hana panel (S3 §8.6, D127, T11.2): the person's last 30 briefings, newest first, and one of
 * them opened read-only. The list is metadata only; the opened one is the server's re-projection for this person now
 * (§8.4), so a fact they lost access to is gone. Read-only: none of the card's actions (no "Đã xem", ⋯, "Hỏi Hana thêm").
 * Its chips are the panel's own (ObjectChip closes the phone sheet as it navigates).
 */

const LINE = 'px-3 py-3 text-sm text-[hsl(var(--h-muted-foreground))]';
const KIND_LABEL: Record<BriefingListItemDTO['kind'], string> = { daily: 'Bản ngày', weekly: 'Bản tuần' };

export function BriefingHistory() {
  const [openId, setOpenId] = useState<Id | null>(null);
  // The one last opened stays tinted when the list comes back, so the reader keeps their place.
  const [lastId, setLastId] = useState<Id | null>(null);
  if (openId !== null) return <BriefingDetail id={openId} onBack={() => setOpenId(null)} />;
  return <BriefingList picked={lastId} onPick={(id) => { setLastId(id); setOpenId(id); }} />;
}

function BriefingList({ picked, onPick }: { picked: Id | null; onPick: (id: Id) => void }) {
  const { data, isPending, isError } = useBriefingList(true);
  // Back from a briefing: focus returns to its row (the list remounts, so the pressed "Quay lại" is gone), once.
  const pickedRef = useRef<HTMLButtonElement>(null);
  const refocused = useRef(false);
  useEffect(() => {
    if (refocused.current || pickedRef.current === null) return;
    refocused.current = true;
    pickedRef.current.focus();
  }, [data]);
  if (isPending) return <p className={LINE}>Đang tải…</p>;
  if (isError) return <p className={LINE}>Không tải được danh sách bản tóm tắt.</p>;
  if (data.length === 0) return <p className={LINE}>Chưa có bản tóm tắt nào.</p>;
  return (
    <ul aria-label="Briefing trước" className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-2">
      {data.map((b) => {
        const current = b.id === picked;
        return (
          <li key={b.id}>
            <button ref={current ? pickedRef : undefined} type="button" data-briefing-history-row=""
              aria-current={current ? 'true' : undefined}
              onClick={() => onPick(b.id)}
              className={cn('flex min-h-11 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors lg:min-h-0',
                current ? 'bg-[hsl(var(--h-primary)/0.1)] text-[hsl(var(--h-primary))]'
                  : 'text-[hsl(var(--h-card-foreground))] hover:bg-[hsl(var(--h-muted))]')}>
              <span className="flex min-w-0 flex-1 flex-col items-start">
                <span data-title="" className="w-full truncate font-medium">{b.title}</span>
                <span className="text-xs text-[hsl(var(--h-muted-foreground))]">
                  {KIND_LABEL[b.kind]} · {formatDateTime(b.composedAt)}
                </span>
              </span>
              {b.seenAt === null && (
                <Badge variant="info" className="flex-none px-[7px] py-0 text-[11px] font-bold">Chưa xem</Badge>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function BriefingDetail({ id, onBack }: { id: Id; onBack: () => void }) {
  const { still } = useAssistant();
  const { data, error, isPending } = useBriefing(id);
  // The pressed row is gone: focus waits on "Quay lại danh sách", then moves to the title once the briefing shows
  // (unless the reader has moved it meanwhile).
  const backRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const loaded = data !== undefined;
  useEffect(() => { backRef.current?.focus(); }, []);
  useEffect(() => {
    if (!loaded) return;
    const at = document.activeElement;
    if (at === backRef.current || at === null || at === document.body) titleRef.current?.focus();
  }, [loaded]);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="px-2 pt-1">
        <button ref={backRef} type="button" onClick={onBack}
          className="flex min-h-11 items-center gap-1 rounded-lg px-2 text-sm font-medium text-[hsl(var(--h-primary))] transition-colors hover:bg-[hsl(var(--h-muted))] lg:min-h-8">
          <ChevronLeft aria-hidden="true" className="size-4" />
          Quay lại danh sách
        </button>
      </div>
      {isPending ? <p className={LINE}>Đang tải…</p>
        : data === undefined ? (
          <p className={LINE}>
            {error?.status === 404 ? 'Bản tóm tắt này không còn xem được.' : 'Không tải được bản tóm tắt.'}
          </p>
        ) : (
          <section role="region" aria-label={data.title} className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 pt-1">
            <BriefingView briefing={data} still={still} phone compact titleRef={titleRef} />
          </section>
        )}
    </div>
  );
}
